import { ReactSVG } from "react-svg";
import { transformRoute } from "helpers";
import { FleetWarehouseRow } from "services/api/fleet/warehouses/types";
import { ButtonLink } from "ui/components/Button/ButtonLink";
import { ButtonMenu } from "ui/components/Button/ButtonMenu";
import ButtonMenuItem from "ui/components/Button/ButtonMenu/ButtonMenuItem";
import ContactCell from "ui/components/Table/TableSearch/components/Cells/ContactCell";
import WorkHourCell from "ui/components/Table/TableSearch/components/Cells/WorkHourCell";
import TableIcon from "ui/components/Table/TableSearch/components/TableIcon";
import { Column, ColumnTypes } from "ui/components/Table/TableSearch/types";
import { Routes as FleetWarehouseRoutes } from "ui/modules/fleet/warehouses/navigation";

/** Stupci pretrage skladišta — isti obrazac kao pretraga kontakata. */
export const WAREHOUSE_SEARCH_COLUMNS = (
  t: (key: string) => string,
  onDeleteClick: (event: React.MouseEvent<HTMLSpanElement>, id: string) => void,
  onEmailClick: (event: React.MouseEvent<HTMLSpanElement>, emailAddress: string) => void,
  onPhoneClick: (event: React.MouseEvent<HTMLSpanElement>, phone: string) => void,
  onGoogleMapClick: (event: React.MouseEvent<HTMLSpanElement>, row: FleetWarehouseRow) => void
): Column[] => [
  {
    title: "",
    key: "warehouse_image",
    alwaysVisible: true,
    field: "image",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => (
      <TableIcon imageUrl={row.image || null} initials={(row.name ?? "").charAt(0).toUpperCase()} />
    ),
  },
  {
    title: "admin.fleet.warehouse.search.name",
    key: "warehouse_name",
    alwaysVisible: true,
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => (
      <div className="min-w-0 max-w-[18rem]">
        <div className="text-md">{row.name}</div>
        {row.description && <div className="text-xs text-slate-500 truncate">{row.description}</div>}
      </div>
    ),
  },
  {
    title: "admin.fleet.warehouse.search.address",
    key: "warehouse_address",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => {
      if (!row.addressLine1 && !row.addressCity) return null;

      const place = [row.addressPostalCode, row.addressCity].filter(Boolean).join(" ");

      return (
        <span
          onClick={(e) => onGoogleMapClick(e, row)}
          className="inline-flex items-start gap-1.5 text-md font-medium text-sky-600 hover:text-sky-700 cursor-pointer"
        >
          <ReactSVG src="/media/icons/map.svg" className="w-4 h-4 mt-0.5 stroke-sky-500 shrink-0" />
          <span>
            <span className="block truncate">{row.addressLine1 || place}</span>
            {row.addressLine1 && place && <span className="block text-xs font-normal text-slate-500">{place}</span>}
          </span>
        </span>
      );
    },
  },
  {
    title: "Radno vrijeme",
    key: "warehouse_working_hours",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => <WorkHourCell workingHours={row.workingHours} />,
  },
  {
    title: "admin.fleet.warehouse.search.contact",
    key: "warehouse_contact",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => {
      const contact = row.contacts?.[0];
      if (!contact) return null;

      return (
        <div className="flex items-center gap-2">
          <ContactCell contact={contact} />
          {row.contacts.length > 1 && <span className="text-xs text-slate-400">+{row.contacts.length - 1}</span>}
        </div>
      );
    },
  },
  {
    title: "Telefon",
    key: "warehouse_phone",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => {
      const phone = row.contacts?.[0]?.primaryContactPhone?.phoneNumber;
      if (!phone) return null;

      return (
        <span
          onClick={(e) => onPhoneClick(e, phone)}
          className="inline-flex items-center gap-1.5 text-md font-medium truncate text-sky-600 hover:text-sky-700 cursor-pointer"
        >
          <ReactSVG src="/media/icons/phone.svg" className="w-4 h-4 stroke-sky-500 shrink-0" />
          {phone}
        </span>
      );
    },
  },
  {
    title: "Email",
    key: "warehouse_email",
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => {
      const email = row.contacts?.[0]?.primaryContactEmail?.emailAddress;
      if (!email) return null;

      return (
        <span
          onClick={(e) => onEmailClick(e, email)}
          className="inline-flex items-center gap-1.5 text-md font-medium truncate text-sky-600 hover:text-sky-700 cursor-pointer"
        >
          <ReactSVG src="/media/icons/email.svg" className="w-4 h-4 stroke-sky-500 shrink-0" />
          {email}
        </span>
      );
    },
  },
  {
    title: "",
    key: "warehouse_actions",
    alwaysVisible: true,
    field: "",
    type: ColumnTypes.custom,
    render: (row: FleetWarehouseRow) => (
      <ButtonMenu endIcon="/media/icons/more-dots.svg" variant="text">
        <ButtonLink
          startIcon="/media/icons/info.svg"
          cssClassName="text-slate-600 hover:bg-blue-100"
          size="full"
          variant="text"
          to={transformRoute(FleetWarehouseRoutes.FleetWarehouseDetailPageRouter, { id: row.id })}
        >
          Detalji skladišta
        </ButtonLink>
        <ButtonLink
          startIcon="/media/icons/tire.svg"
          cssClassName="text-slate-600 hover:bg-blue-100"
          size="full"
          variant="text"
          to={transformRoute(FleetWarehouseRoutes.FleetWarehouseTireSetSearchPageRouter, { id: row.id })}
        >
          Gume na skladištu
        </ButtonLink>
        <ButtonMenuItem
          startIcon="/media/icons/trash.svg"
          variant="remove"
          className="text-slate-600 hover:bg-blue-100"
          onClick={(e) => onDeleteClick(e, row.id)}
        >
          Obriši
        </ButtonMenuItem>
      </ButtonMenu>
    ),
  },
];
